import React, { useMemo, useRef } from "react";

import { SxProps, Stack, Menu, MenuItem, IconButton, styled, ListItemIcon, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LoginIcon from "@mui/icons-material/Login";
import { cacheService, searchService, userService } from "../../../Core/Api/services";
import Logo from "../../Logo/logo-svg";
import { useNavigate } from "react-router";
import { routePaths } from "../../../Router/router";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { SearchRequestNative } from "../../../Core/Api/Types/search-request-native";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_CLOSED } from "../sidebar";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";

export interface SidebarBrandProps {
  sx?: SxProps;
  minified?: boolean;
  drawerOpen: boolean;
  platformId?: number;
}

const SidebarBrand: React.FC<SidebarBrandProps> = ({ drawerOpen, platformId, minified }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const logoRef = useRef<any>(undefined);
  const isLoggedIn = cacheService.IsLoggedIn();
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const navigate = useNavigate();
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const enqueueCustomSnackbar = useCustomSnackbar();

  const sharedAccounts = cacheService.GetSharedAccounts();
  const isEmulating = cacheService.GetIsEmulate();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const mode = cacheService.GetPlatformMode();
  const pid = cacheService.GetUserInfo()?.platformId;

  const tdfLogo = pid === 1 && !UserHasAcl(AclTypes.aclSuperUser);

  const redirectUrl = useMemo(() => {
    switch (mode) {
      case PlatformMode.Distribution:
        return routePaths.home.path;
      case PlatformMode.Browser:
        return routePaths.drive.path;
      default:
        return routePaths.home.path;
    }
  }, [mode]);

  const LoginSharedAccount = (userId: number) => {
    userService.LoginSharedCrossAccount(userId).then((response) => {
      if (response.success) {
        userService.SetDataOnLoginSuccess().then(async function () {
          enqueueCustomSnackbar(t("alert:success.switchedPlatform"), "success");

          if (
            cacheService.IsDriveEnabled() &&
            (UserHasAcl(AclTypes.aclDrive) || cacheService.GetPlatformMode() === PlatformMode.Browser) &&
            response.platformHeadObjectId
          ) {
            cacheService.SetPlatformDriveHeadId(response.platformHeadObjectId);
          }
          navigate(redirectUrl);
          window.location.reload();
        });
      } else {
        enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
      }
    });
  };

  return (
    <Stack
      direction={"row"}
      overflow={"hidden"}
      alignItems="center"
      justifyContent={drawerOpen ? "space-between" : "center"}
      spacing={0.5}
      //   ml={drawerOpen ? 0 : "-6px !important"}
      sx={{
        height: GlobalConst.HEADER_HEIGHT,
        width: drawerOpen ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_CLOSED,
        position: "fixed",
      }}
    >
      <Stack
        px={drawerOpen ? 1.5 : 0.5}
        ref={logoRef}
        spacing={0.5}
        direction="row"
        alignItems="center"
        justifyContent={drawerOpen ? "flex-start" : "center"}
        flexGrow="1"
        height="1"
      >
        {/* That's here the ONLY non-generic code to write text "tdf" for platform 1 */}
        <Logo
          small={!drawerOpen}
          color={theme.palette.text.primary}
          href="/"
          onClick={!drawerOpen ? () => setAnchorEl(logoRef.current) : undefined}
          sx={{ justifyContent: drawerOpen ? "flex-start" : "center", px: drawerOpen ? 1 : 0 }}
          tdfLogo={tdfLogo}
        />
      </Stack>

      {isLoggedIn && (
        <>
          {drawerOpen && (
            <IconButton
              size="small"
              aria-label="Switch brand"
              id="sidebar-brand-button"
              aria-controls={open ? "sidebar-brand-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleClick}
              sx={() =>
                drawerOpen
                  ? { position: "relative", right: 12 }
                  : { zIndex: 1300, position: "absolute", top: "18px", left: "64px" }
              }
            >
              <ExpandMoreIcon />
            </IconButton>
          )}
          <StyledMenu
            id="sidebar-brand-menu"
            MenuListProps={{
              "aria-labelledby": "sidebar-brand-button",
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            {(sharedAccounts.length === 0 || isEmulating) && (
              <Stack width={"100%"} alignItems={"center"} justifyContent="center" py={0.5}>
                <Typography variant="body1">{t("menu:item.noOtherPlatformToSwitch")}</Typography>
              </Stack>
            )}
            {sharedAccounts.length > 0 &&
              !isEmulating &&
              sharedAccounts
                .filter((x) => pid !== null && x.platformId !== pid)
                .map((account) => (
                  <StyledMenuItem key={account.userId} onClick={() => LoginSharedAccount(account.userId)}>
                    <ListItemIcon>
                      <LoginIcon />
                    </ListItemIcon>
                    <Stack>
                      <Typography>{account.platformName}</Typography>
                      <Typography color="text.tier" variant="body2" overflow="hidden">
                        {account.userName}
                      </Typography>
                    </Stack>
                  </StyledMenuItem>
                ))}
            {/*<StyledMenuItem>
              <ListItemIcon>
                <Avatar src="https://picsum.photos/48/48" sx={{ width: 24, height: 24 }} />
              </ListItemIcon>
              SND - M6
            </StyledMenuItem>
            <StyledMenuItem>
              <ListItemIcon>
                <Avatar src="https://picsum.photos/64/64" sx={{ width: 24, height: 24 }} />
              </ListItemIcon>
              Studio Canal
            </StyledMenuItem>
            <StyledMenuItem>
              <ListItemIcon>
                <Avatar src="https://picsum.photos/48/48" sx={{ width: 24, height: 24 }} />
              </ListItemIcon>
              Newen
        </StyledMenuItem>*/}
          </StyledMenu>
        </>
      )}
    </Stack>
  );
};

export default SidebarBrand;

const StyledMenu = styled(Menu)(({ theme }) => ({
  ".MuiMenu-paper": {
    border: "none",
    padding: theme.spacing(1),
    background: theme.palette.background.paper,
    width: 322,
    overflow: "auto",
    maxHeight: "650px",
    ...theme.helpers(theme).scrollBar,
    ...theme.helpers(theme).popoverMenuHighlights,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  "&.MuiMenuItem-root": {
    borderBottom: "none",
    padding: theme.spacing(0.5),
    borderRadius: 6,
    transition: "background .3s",
    "& + .MuiMenuItem-root": {
      marginTop: theme.spacing(1),
    },
    "&:hover": {
      background: theme.palette.backgroundAlpha.hover,
    },
  },
}));
